import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Grid,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  Divider,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import {
  Delete,
  Add,
  Block,
  CheckCircle,
  People,
  Refresh,
  AdminPanelSettings,
  SupervisorAccount,
  PointOfSale,
  Badge,
  DeliveryDining,
  Person,
  Shield,
  Edit,
  Visibility,
  VisibilityOff,
  LockReset,
  Email as EmailIcon,
  VpnKey,
  ManageAccounts,
} from '@mui/icons-material';
import userSyncService from '../../services/userSyncService';

interface User {
  id: number;
  uid?: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
  status: string;
  // 4-digit PIN for POS critical-action approval (refund / cancel / discount / void).
  // Stored in the `users` collection alongside the user doc.
  manager_pin?: string;
}

const ROLE_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode; description: string }> = {
  admin: {
    label: 'Admin',
    color: '#1a1d2e',
    bg: '#d32f2f',
    icon: <AdminPanelSettings sx={{ fontSize: 16 }} />,
    description: 'Full access to all features, settings, and user management',
  },
  manager: {
    label: 'Manager',
    color: '#1a1d2e',
    bg: '#7b1fa2',
    icon: <SupervisorAccount sx={{ fontSize: 16 }} />,
    description: 'Manage staff, orders, and view reports',
  },
  cashier: {
    label: 'Cashier',
    color: '#1a1d2e',
    bg: '#1565c0',
    icon: <PointOfSale sx={{ fontSize: 16 }} />,
    description: 'Process orders and handle payments',
  },
  staff: {
    label: 'Staff',
    color: '#1a1d2e',
    bg: '#2e7d32',
    icon: <Badge sx={{ fontSize: 16 }} />,
    description: 'Take orders and manage tables (own tables only)',
  },
  rider: {
    label: 'Rider',
    color: '#1a1d2e',
    bg: '#e65100',
    icon: <DeliveryDining sx={{ fontSize: 16 }} />,
    description: 'Delivery driver — view and manage deliveries',
  },
  customer: {
    label: 'Customer',
    color: '#9ca3af',
    bg: '#e0e0e0',
    icon: <Person sx={{ fontSize: 16 }} />,
    description: 'App customer account',
  },
};

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingRoleUser, setEditingRoleUser] = useState<string | null>(null); // uid of user being edited
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'staff',
    avatar: '',
    password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  // Manager PIN editor state — admin sets a 4-digit PIN per manager/admin user.
  // That PIN is what the POS ManagerPinDialog verifies on refunds, voids,
  // cancellations and >10% discounts.
  const [pinDialogUser, setPinDialogUser] = useState<User | null>(null);
  const [pinInput, setPinInput] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState('');

  // Password change dialog state — admin can either send a reset email
  // (secure, user sets their own) or set a new password directly.
  const [passwordDialogUser, setPasswordDialogUser] = useState<User | null>(null);
  const [passwordMode, setPasswordMode] = useState<'email' | 'direct'>('email');
  const [directPassword, setDirectPassword] = useState('');
  const [directPasswordConfirm, setDirectPasswordConfirm] = useState('');
  const [showDirectPassword, setShowDirectPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Profile edit dialog state — admin edits name + phone + email for any user.
  const [editProfileUser, setEditProfileUser] = useState<User | null>(null);
  const [editProfileForm, setEditProfileForm] = useState({ name: '', email: '', phone: '' });
  const [editProfileSaving, setEditProfileSaving] = useState(false);
  const [editProfileError, setEditProfileError] = useState('');

  const openPinDialog = (user: User) => {
    setPinDialogUser(user);
    setPinInput(user.manager_pin || '');
    setPinError('');
  };
  const closePinDialog = () => {
    setPinDialogUser(null);
    setPinInput('');
    setPinError('');
  };

  const savePin = async (clear: boolean = false) => {
    if (!pinDialogUser?.uid) { setPinError('No user selected'); return; }
    const pinToSave = clear ? '' : pinInput.trim();
    if (!clear) {
      if (!/^\d{4}$/.test(pinToSave)) {
        setPinError('PIN must be exactly 4 digits');
        return;
      }
      // Collision check: refuse if another user already has this PIN
      const clash = users.find(u => u.uid !== pinDialogUser.uid && u.manager_pin === pinToSave);
      if (clash) {
        setPinError(`PIN is already used by ${clash.name} — pick a unique PIN`);
        return;
      }
    }
    setPinSaving(true);
    setPinError('');
    try {
      const { doc, updateDoc, setDoc, getDoc, collection, query, where, getDocs } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      let primaryWriteOk = false;

      // 1) users/{uid} — this is the PRIMARY store the POS looks up first.
      //    If the doc doesn't exist we create it with the user's current role
      //    (don't stomp a missing role with 'customer' from the listAuthUsers fallback).
      try {
        const ref = doc(db, 'users', pinDialogUser.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          // Existing doc: update only the PIN — preserve whatever role is already there.
          await updateDoc(ref, { manager_pin: pinToSave, updated_at: new Date().toISOString() });
        } else {
          // New doc: use the best role we know. Prefer pinDialogUser.role unless
          // it's the listAuthUsers fallback 'customer'. In that case leave it unset
          // so POS PIN verify relies on the admin-email whitelist instead.
          const roleToSet = pinDialogUser.role && pinDialogUser.role !== 'customer' ? pinDialogUser.role : undefined;
          const seed: any = {
            uid: pinDialogUser.uid, email: pinDialogUser.email, name: pinDialogUser.name,
            manager_pin: pinToSave,
            created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
          };
          if (roleToSet) seed.role = roleToSet;
          await setDoc(ref, seed, { merge: true });
        }
        primaryWriteOk = true;
      } catch (e: any) {
        console.error('[PIN] users/ write failed:', e);
        throw new Error(`Could not write PIN to users/: ${e?.message || e}`);
      }

      // 2) Mirror to admin_users/* (POS falls back to this if users/ lookup misses)
      try {
        const aref = doc(db, 'admin_users', pinDialogUser.uid);
        const asnap = await getDoc(aref);
        if (asnap.exists()) {
          await updateDoc(aref, { manager_pin: pinToSave, updated_at: new Date().toISOString() });
        } else {
          await setDoc(aref, {
            uid: pinDialogUser.uid, email: pinDialogUser.email, name: pinDialogUser.name,
            role: pinDialogUser.role, manager_pin: pinToSave,
            created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
          }, { merge: true });
        }
      } catch (e) { console.warn('[PIN] admin_users/ mirror failed (non-fatal):', e); }

      // 3) Mirror to pos_users/* IF the user has a doc there — don't create one
      //    just for the PIN, but keep existing records in sync.
      try {
        const posSnap = await getDocs(query(collection(db, 'pos_users'), where('email', '==', pinDialogUser.email)));
        if (!posSnap.empty) {
          await updateDoc(posSnap.docs[0].ref, {
            manager_pin: pinToSave, updated_at: new Date().toISOString(),
          });
        }
      } catch (e) { console.warn('[PIN] pos_users/ mirror failed (non-fatal):', e); }

      if (!primaryWriteOk) throw new Error('PIN not saved anywhere — check Firestore rules and try again.');

      // Update local list so the chip updates immediately
      setUsers(prev => prev.map(u => u.uid === pinDialogUser.uid ? { ...u, manager_pin: pinToSave } : u));
      setSnackbar({
        open: true,
        message: clear
          ? `PIN removed for ${pinDialogUser.name}`
          : `PIN saved for ${pinDialogUser.name}. They can now authorize refunds, voids & discounts on the POS.`,
        severity: 'success',
      });
      closePinDialog();
    } catch (err: any) {
      setPinError(err.message || 'Failed to save PIN');
    } finally {
      setPinSaving(false);
    }
  };

  const fetchUsers = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      // Get fresh ID token from current Firebase Auth user (SessionManager token may be expired)
      const { getAuth } = await import('firebase/auth');
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('No authentication token found. Please log in again.');
      const token = await currentUser.getIdToken(true);

      const response = await fetch('__SAHEEH_FUNCTIONS_URL_UNSET__/listAuthUsers', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.users) {
        const usersData = data.users.map((user: any, index: number) => ({
          id: index + 1,
          uid: user.uid,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.displayName || user.email?.split('@')[0] || 'User',
          email: user.email,
          role: user.role || 'customer',
          avatar: '',
          status: user.is_active && !user.disabled ? 'active' : 'inactive',
          manager_pin: user.manager_pin || '',
        }));
        setUsers(usersData);
      } else {
        setUsers([]);
      }
    } catch (e: any) {
      console.error('Users fetch error:', e);
      setError(e.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleAddUser = async () => {
    try {
      setSyncLoading(true);
      const nameParts = newUser.name.trim().split(' ');
      const first_name = nameParts[0] || '';
      const last_name = nameParts.slice(1).join(' ') || '';

      if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        setSyncLoading(false);
        return;
      }

      if (newUser.password.length < 6) {
        setSnackbar({ open: true, message: 'Password must be at least 6 characters', severity: 'error' });
        setSyncLoading(false);
        return;
      }

      if (newUser.password !== confirmPassword) {
        setSnackbar({ open: true, message: 'Passwords do not match', severity: 'error' });
        setSyncLoading(false);
        return;
      }

      const syncResult = await userSyncService.syncUserToFirestore({
        email: newUser.email.trim(),
        first_name,
        last_name,
        role: newUser.role as any,
        is_active: true,
        password: newUser.password,
      });

      if (syncResult.success) {
        await fetchUsers(false);
        setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
        setOpenAddDialog(false);
        setNewUser({ name: '', email: '', role: 'staff', avatar: '', password: '' });
        setConfirmPassword('');
        setShowPassword(false);
        setShowConfirmPassword(false);
      } else {
        throw new Error(syncResult.error || 'Failed to create user');
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to create user', severity: 'error' });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const uid = (user as any).uid;
      if (!uid) throw new Error('User UID not found');

      const { getAuth } = await import('firebase/auth');
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('No authentication token');
      const token = await currentUser.getIdToken(true);

      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      const response = await fetch('__SAHEEH_FUNCTIONS_URL_UNSET__/toggleAuthUser', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, disabled: newStatus === 'inactive' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to update user status');
      }

      await fetchUsers(false);
      setSnackbar({ open: true, message: `User ${newStatus === 'active' ? 'activated' : 'deactivated'}`, severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update status', severity: 'error' });
    }
  };

  const handleSendPasswordReset = async (user: User) => {
    if (!user.email) {
      setSnackbar({ open: true, message: 'User has no email address', severity: 'error' });
      return;
    }
    try {
      const { getAuth, sendPasswordResetEmail } = await import('firebase/auth');
      const auth = getAuth();
      auth.languageCode = 'ar';
      await sendPasswordResetEmail(auth, user.email);
      setSnackbar({
        open: true,
        message: `Reset email sent to ${user.email}. The user will receive a secure link from Firebase to set a new password.`,
        severity: 'success',
      });
    } catch (error: any) {
      const code = error?.code || '';
      const msg = code === 'auth/user-not-found'
        ? `No Firebase Auth account found for ${user.email}`
        : error?.message || 'Failed to send reset email';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  // Opens the password dialog with the mode selector.
  const openPasswordDialog = (user: User) => {
    setPasswordDialogUser(user);
    setPasswordMode('email');
    setDirectPassword('');
    setDirectPasswordConfirm('');
    setShowDirectPassword(false);
    setPasswordError('');
  };

  const closePasswordDialog = () => {
    setPasswordDialogUser(null);
    setDirectPassword('');
    setDirectPasswordConfirm('');
    setPasswordError('');
  };

  // Saves a new password directly by calling the `updateUserPassword` Cloud Function.
  const savePasswordDirect = async () => {
    if (!passwordDialogUser?.email) { setPasswordError('User has no email'); return; }
    if (directPassword.length < 6) { setPasswordError('Password must be at least 6 characters'); return; }
    if (directPassword !== directPasswordConfirm) { setPasswordError('Passwords do not match'); return; }
    setPasswordSaving(true);
    setPasswordError('');
    try {
      const { getAuth } = await import('firebase/auth');
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('Not signed in');
      const token = await currentUser.getIdToken(true);
      const response = await fetch('__SAHEEH_FUNCTIONS_URL_UNSET__/updateUserPassword', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: passwordDialogUser.email, password: directPassword }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update password');
      }
      setSnackbar({
        open: true,
        message: `Password updated for ${passwordDialogUser.email}. Share it with the user securely — they will use it on next login.`,
        severity: 'success',
      });
      closePasswordDialog();
    } catch (e: any) {
      setPasswordError(e?.message || 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Sends the Firebase reset email from inside the dialog.
  const sendResetFromDialog = async () => {
    if (!passwordDialogUser) return;
    setPasswordSaving(true);
    await handleSendPasswordReset(passwordDialogUser);
    setPasswordSaving(false);
    closePasswordDialog();
  };

  // Opens the profile edit dialog prefilled with the user's current values.
  const openEditProfile = (user: User) => {
    setEditProfileUser(user);
    setEditProfileForm({
      name: user.name || '',
      email: user.email || '',
      phone: (user as any).phone || '',
    });
    setEditProfileError('');
  };

  const closeEditProfile = () => {
    setEditProfileUser(null);
    setEditProfileError('');
  };

  // Saves name/phone/email across the user's Firestore collections + Firebase Auth.
  // Mirrors the admin Drivers page logic but works for any user type.
  const saveProfileEdit = async () => {
    if (!editProfileUser) return;
    const name = editProfileForm.name.trim();
    const email = editProfileForm.email.trim();
    const phone = editProfileForm.phone.trim();
    if (!name) { setEditProfileError('Name is required'); return; }
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) { setEditProfileError('Valid email is required'); return; }

    setEditProfileSaving(true);
    setEditProfileError('');
    try {
      const { getFirestore, doc: docFn, updateDoc, collection: coll, query, where, getDocs } = await import('../../services/firestoreCompat');
      const { getAuth } = await import('firebase/auth');
      const db = getFirestore();
      const uid = (editProfileUser as any).uid;
      const nowPatch: any = { name, email, phone, updated_at: new Date() };

      // Update the primary collection doc (users / drivers / pos_users / admin_users) by uid.
      const collections = ['users', 'drivers', 'pos_users', 'admin_users'];
      for (const cname of collections) {
        try {
          if (uid) {
            await updateDoc(docFn(db, cname, uid), nowPatch);
          }
        } catch {}
        // Also find by the OLD email (for legacy docs keyed differently).
        try {
          const q = query(coll(db, cname), where('email', '==', editProfileUser.email));
          const snap = await getDocs(q);
          for (const d of snap.docs) {
            await updateDoc(d.ref, nowPatch);
          }
        } catch {}
      }

      // If email changed, propagate to Firebase Auth via Cloud Function (Admin SDK).
      if (email !== editProfileUser.email && uid) {
        const currentUser = getAuth().currentUser;
        const token = currentUser ? await currentUser.getIdToken(true) : null;
        if (token) {
          // We reuse updateUserPassword pattern — but there's no dedicated email-change
          // function. Best-effort: inform admin they also need to send reset to new email
          // so user reconfirms. Auth email stays at old address until user action.
          setSnackbar({
            open: true,
            message: `Profile updated. Email change reflected in Firestore only — for Firebase login email, send a reset email to the new address after the user confirms it.`,
            severity: 'success',
          });
        }
      } else {
        setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
      }

      await fetchUsers(false);
      closeEditProfile();
    } catch (e: any) {
      setEditProfileError(e?.message || 'Failed to save profile');
    } finally {
      setEditProfileSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      const uid = (selectedUser as any).uid;
      if (!uid) throw new Error('User UID not found');

      const { getAuth } = await import('firebase/auth');
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('No authentication token');
      const token = await currentUser.getIdToken(true);

      const response = await fetch('__SAHEEH_FUNCTIONS_URL_UNSET__/deleteAuthUser', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to delete user');
      }

      await fetchUsers(false);
      setOpenDeleteDialog(false);
      setSelectedUser(null);
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to delete user', severity: 'error' });
    }
  };

  const handleRoleChange = async (user: User, newRole: string) => {
    try {
      const uid = (user as any).uid;
      if (!uid) throw new Error('User UID not found');

      // Update role in Firestore users collection
      const { doc, updateDoc } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      // Update in 'users' collection
      try {
        await updateDoc(doc(db, 'users', uid), { role: newRole, updated_at: new Date().toISOString() });
      } catch (e) {
        console.warn('Could not update users collection:', e);
      }

      // Update in 'admin_users' collection
      try {
        await updateDoc(doc(db, 'admin_users', uid), { role: newRole, updated_at: new Date().toISOString() });
      } catch (e) {
        console.warn('Could not update admin_users collection:', e);
      }

      setEditingRoleUser(null);
      await fetchUsers(false);
      setSnackbar({ open: true, message: `${user.name}'s role updated to ${ROLE_CONFIG[newRole]?.label || newRole}`, severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update role', severity: 'error' });
    }
  };

  // Stats
  const adminCount = users.filter(u => u.role === 'admin').length;
  const staffCount = users.filter(u => ['staff', 'cashier', 'manager'].includes(u.role)).length;
  const activeCount = users.filter(u => u.status === 'active').length;

  const getRoleConfig = (role: string) => ROLE_CONFIG[role] || ROLE_CONFIG.customer;

  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (name[0] || '?').toUpperCase();
  };

  const getAvatarColor = (role: string) => {
    const config = getRoleConfig(role);
    return config.bg;
  };

  return (
    <AdminLayout>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: { xs: 2, md: 3 }, flexWrap: 'wrap', gap: { xs: 1.5, md: 0 } }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a2e', letterSpacing: '-0.5px', fontSize: { xs: '1.4rem', md: '2.125rem' } }}>
              Team Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
              Manage user accounts, roles, and permissions
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: { xs: 1, md: 1.5 }, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => fetchUsers()}
              disabled={loading}
              sx={{ borderColor: '#3a3d52 !important', color: '#9ca3af !important', backgroundColor: 'transparent !important', '&:hover': { borderColor: '#00C896 !important', color: '#00C896 !important', backgroundColor: 'rgba(0, 200, 150,0.08) !important' } }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                bgcolor: '#00C896', color: '#1a1d2e',
                px: { xs: 1.5, md: 3 },
                '&:hover': { bgcolor: '#7bc912' },
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Add User
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Users', value: users.length, icon: <People />, color: '#1a1a2e' },
            { label: 'Admins', value: adminCount, icon: <Shield />, color: '#d32f2f' },
            { label: 'Staff Members', value: staffCount, icon: <Badge />, color: '#4CAF50' },
            { label: 'Active', value: activeCount, icon: <CheckCircle />, color: '#2196F3' },
          ].map((stat) => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ borderRadius: '12px', border: '1px solid #3a3d52', boxShadow: 'none' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${stat.color}12`, color: stat.color,
                  }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', lineHeight: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>{error}</Alert>}

        {/* Users Table - Desktop */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Paper sx={{ borderRadius: '12px', border: '1px solid #3a3d52', boxShadow: 'none', overflow: 'hidden' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <People sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">No users found</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1e2139' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => {
                      const roleConfig = getRoleConfig(user.role);
                      const isEditing = editingRoleUser === (user as any).uid;

                      return (
                        <TableRow
                          key={user.id}
                          hover
                          sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: '#1e2139' } }}
                        >
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{
                                bgcolor: getAvatarColor(user.role),
                                width: 40, height: 40,
                                fontSize: '0.85rem', fontWeight: 700,
                              }}>
                                {getInitials(user.name)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                                  {user.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                  {user.role === 'admin' ? 'Administrator' : user.role === 'staff' ? 'Staff Member' : user.role === 'manager' ? 'Manager' : user.role === 'cashier' ? 'Cashier' : user.role}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Typography variant="body2" sx={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {user.email}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            {isEditing ? (
                              <Select
                                value={user.role}
                                onChange={(e: SelectChangeEvent) => handleRoleChange(user, e.target.value)}
                                onClose={() => setEditingRoleUser(null)}
                                size="small"
                                autoFocus
                                open
                                sx={{ minWidth: 130, fontSize: '0.85rem' }}
                              >
                                {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
                                  <MenuItem key={key} value={key}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {cfg.icon}
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{cfg.label}</Typography>
                                        <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', fontSize: '0.65rem' }}>{cfg.description}</Typography>
                                      </Box>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Tooltip title={roleConfig.description} arrow placement="top">
                                <Chip
                                  icon={<Box sx={{ display: 'flex', color: 'inherit', ml: 0.5 }}>{roleConfig.icon}</Box>}
                                  label={roleConfig.label}
                                  size="small"
                                  sx={{
                                    bgcolor: roleConfig.bg,
                                    color: roleConfig.color,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                  onClick={() => setEditingRoleUser((user as any).uid || null)}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                bgcolor: user.status === 'active' ? '#4caf50' : '#bdbdbd',
                              }} />
                              <Typography variant="body2" sx={{
                                fontWeight: 500,
                                color: user.status === 'active' ? '#2e7d32' : '#999',
                                fontSize: '0.85rem',
                              }}>
                                {user.status === 'active' ? 'Active' : 'Inactive'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                              {/* Manager PIN — only admin/manager can authorize critical
                                  POS actions. Giving a cashier a PIN is a bad idea (they'd
                                  approve their own cancels) so we hide the shield for them. */}
                              {['admin', 'manager'].includes(user.role) && (
                                <Tooltip title={user.manager_pin ? `Change PIN (currently set)` : 'Set Manager PIN'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => openPinDialog(user)}
                                    sx={{
                                      color: user.manager_pin ? '#FFC107' : '#6b7280',
                                      bgcolor: user.manager_pin ? 'rgba(255,193,7,0.14)' : 'transparent',
                                      '&:hover': { color: '#FFC107', bgcolor: 'rgba(255,193,7,0.2)' },
                                    }}
                                  >
                                    <AdminPanelSettings sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Change Role">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingRoleUser((user as any).uid || null)}
                                  sx={{ color: '#9ca3af', '&:hover': { color: '#2196F3', bgcolor: 'rgba(33, 150, 243, 0.12)' } }}
                                >
                                  <Edit sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={user.status === 'active' ? 'Deactivate' : 'Activate'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleStatus(user)}
                                  sx={{
                                    color: user.status === 'active' ? '#e65100' : '#2e7d32',
                                    '&:hover': {
                                      bgcolor: user.status === 'active' ? 'rgba(255, 152, 0, 0.12)' : 'rgba(76, 175, 80, 0.12)',
                                    },
                                  }}
                                >
                                  {user.status === 'active' ? <Block sx={{ fontSize: 18 }} /> : <CheckCircle sx={{ fontSize: 18 }} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit profile (name, email, phone)">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditProfile(user)}
                                  sx={{ color: '#6b7280', '&:hover': { color: '#2196F3', bgcolor: 'rgba(33, 150, 243, 0.12)' } }}
                                >
                                  <ManageAccounts sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Change password (email link or set directly)">
                                <IconButton
                                  size="small"
                                  onClick={() => openPasswordDialog(user)}
                                  sx={{ color: '#6b7280', '&:hover': { color: '#00C896', bgcolor: 'rgba(0, 200, 150, 0.12)' } }}
                                >
                                  <LockReset sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete User">
                                <IconButton
                                  size="small"
                                  onClick={() => { setSelectedUser(user); setOpenDeleteDialog(true); }}
                                  sx={{ color: '#6b7280', '&:hover': { color: '#d32f2f', bgcolor: 'rgba(244, 67, 54, 0.12)' } }}
                                >
                                  <Delete sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>

        {/* Users Cards - Mobile */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <People sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">No users found</Typography>
            </Box>
          ) : users.map((user) => (
            <Box key={user.id} sx={{ backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: '10px', p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#00C896', color: '#1a1d2e', fontSize: '0.8rem', fontWeight: 700 }}>{user.name?.charAt(0)?.toUpperCase() || 'U'}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.88rem', fontFamily: '"Poppins"' }}>{user.name || 'Unknown'}</Typography>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.7rem', fontFamily: '"Poppins"', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</Typography>
                </Box>
                <Chip label={user.role} size="small" sx={{ fontSize: '0.65rem', height: 22, fontWeight: 600 }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5, borderTop: '1px solid #2a2d3e' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: user.status === 'active' ? '#4CAF50' : '#999' }} />
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.72rem', fontFamily: '"Poppins"' }}>{user.status === 'active' ? 'Active' : 'Inactive'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => setEditingRoleUser((user as any).uid || null)} sx={{ color: '#2196F3', width: 28, height: 28 }}><Edit sx={{ fontSize: 16 }} /></IconButton>
                  <IconButton size="small" onClick={() => handleToggleStatus(user)} sx={{ color: user.status === 'active' ? '#FF9800' : '#4CAF50', width: 28, height: 28 }}>
                    {user.status === 'active' ? <Block sx={{ fontSize: 16 }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
                  </IconButton>
                  <IconButton size="small" onClick={() => openEditProfile(user)} sx={{ color: '#2196F3', width: 28, height: 28 }}><ManageAccounts sx={{ fontSize: 16 }} /></IconButton>
                  <IconButton size="small" onClick={() => openPasswordDialog(user)} sx={{ color: '#00C896', width: 28, height: 28 }}><LockReset sx={{ fontSize: 16 }} /></IconButton>
                  <IconButton size="small" onClick={() => { setSelectedUser(user); setOpenDeleteDialog(true); }} sx={{ color: '#F44336', width: 28, height: 28 }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Role Legend */}
        <Paper sx={{ mt: 3, p: 2.5, borderRadius: '12px', border: '1px solid #3a3d52', boxShadow: 'none' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1.5 }}>
            Role Permissions
          </Typography>
          <Grid container spacing={1.5}>
            {Object.entries(ROLE_CONFIG).filter(([key]) => key !== 'customer').map(([key, cfg]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Chip
                    icon={<Box sx={{ display: 'flex', color: 'inherit', ml: 0.5 }}>{cfg.icon}</Box>}
                    label={cfg.label}
                    size="small"
                    sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem', '& .MuiChip-icon': { color: 'inherit' }, minWidth: 90 }}
                  />
                  <Typography variant="caption" sx={{ color: '#777', lineHeight: 1.4, pt: 0.25 }}>
                    {cfg.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      {/* Add User Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => {
          if (syncLoading) return;
          setOpenAddDialog(false);
          setConfirmPassword('');
          setShowPassword(false);
          setShowConfirmPassword(false);
        }}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add New Team Member</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Full Name" value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required placeholder="e.g. Max Mustermann"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Email" type="email" value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required placeholder="e.g. max@saheehpos.com"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Password"
                type={showPassword ? 'text' : 'password'}
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required helperText="Minimum 6 characters"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                error={!!confirmPassword && confirmPassword !== newUser.password}
                helperText={
                  confirmPassword && confirmPassword !== newUser.password
                    ? 'Passwords do not match'
                    : 'Re-enter the password to confirm'
                }
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  label="Role"
                  sx={{ borderRadius: '10px' }}
                >
                  {Object.entries(ROLE_CONFIG).filter(([key]) => key !== 'customer').map(([key, cfg]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ color: cfg.bg, display: 'flex' }}>{cfg.icon}</Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{cfg.label}</Typography>
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>{cfg.description}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 1.5, md: 3 }, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpenAddDialog(false);
              setConfirmPassword('');
              setShowPassword(false);
              setShowConfirmPassword(false);
            }}
            disabled={syncLoading}
            sx={{ color: '#9ca3af' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUser} variant="contained"
            disabled={
              syncLoading ||
              !newUser.name.trim() ||
              !newUser.email.trim() ||
              !newUser.password.trim() ||
              newUser.password.length < 6 ||
              newUser.password !== confirmPassword
            }
            sx={{ bgcolor: '#00C896', color: '#1a1d2e', borderRadius: '10px', px: { xs: 1.5, md: 3 }, '&:hover': { bgcolor: '#7bc912' } }}
          >
            {syncLoading ? <CircularProgress size={20} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 1.5, md: 3 }, pb: 2.5 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#9ca3af' }}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" sx={{ borderRadius: '10px' }}>
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manager PIN Editor */}
      <Dialog open={!!pinDialogUser} onClose={() => !pinSaving && closePinDialog()} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: '#1a1d2e', border: '2px solid #FFC107' } }}>
        <DialogTitle sx={{ color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #2a2d42' }}>
          <AdminPanelSettings sx={{ color: '#FFC107', fontSize: 28 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Manager PIN</Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem' }}>
              {pinDialogUser?.name} · {pinDialogUser?.role}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.85rem', mb: 2, lineHeight: 1.5 }}>
            This 4-digit PIN is used on the POS to authorize refunds, voids,
            cancellations and large discounts. Only this user will know it —
            they enter it when prompted by the Manager Approval dialog.
          </Typography>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={4}
            value={pinInput}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 4);
              setPinInput(v);
              setPinError('');
            }}
            placeholder="••••"
            autoFocus
            style={{
              width: '100%', padding: '14px 16px', borderRadius: 10,
              border: `2px solid ${pinError ? '#ef5350' : '#3a3d52'}`,
              background: '#1e2130', color: '#FFC107',
              fontSize: '1.8rem', fontWeight: 700, letterSpacing: '12px',
              textAlign: 'center', outline: 'none', boxSizing: 'border-box',
            }}
          />
          {pinError && (
            <Typography sx={{ color: '#ef5350', fontSize: '0.8rem', mt: 1, textAlign: 'center' }}>{pinError}</Typography>
          )}
          <Typography sx={{ color: '#6b7280', fontSize: '0.72rem', mt: 2, textAlign: 'center', fontStyle: 'italic' }}>
            🔒 Share this PIN with the user privately. Change it if they leave the team.
          </Typography>
          {pinDialogUser?.manager_pin && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(76,175,80,0.1)', borderRadius: 2, border: '1px solid rgba(76,175,80,0.3)' }}>
              <Typography sx={{ color: '#4CAF50', fontSize: '0.78rem', fontWeight: 600 }}>
                ✓ PIN is currently set. Changing it will invalidate the old one immediately.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #2a2d42', gap: 1 }}>
          <Button onClick={closePinDialog} disabled={pinSaving} sx={{ color: '#9ca3af', textTransform: 'none' }}>
            Cancel
          </Button>
          {pinDialogUser?.manager_pin && (
            <Button onClick={() => savePin(true)} disabled={pinSaving} color="error" sx={{ textTransform: 'none' }}>
              Remove PIN
            </Button>
          )}
          <Button onClick={() => savePin(false)} disabled={pinSaving || pinInput.length !== 4} variant="contained"
            sx={{ bgcolor: '#FFC107', color: '#1a1d2e', fontWeight: 700, textTransform: 'none',
              '&:hover': { bgcolor: '#FFB300' }, '&.Mui-disabled': { bgcolor: 'rgba(255,193,7,0.3)', color: 'rgba(26,29,46,0.6)' } }}>
            {pinSaving ? 'Saving…' : (pinDialogUser?.manager_pin ? 'Update PIN' : 'Save PIN')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password change dialog */}
      <Dialog
        open={!!passwordDialogUser}
        onClose={() => !passwordSaving && closePasswordDialog()}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#252838', borderRadius: '14px', border: '1px solid #3a3d52' } }}
      >
        <DialogTitle sx={{ color: '#e0e0e0', fontWeight: 700, borderBottom: '1px solid #3a3d52', pb: 1.5 }}>
          Change password for {passwordDialogUser?.name || passwordDialogUser?.email}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
            <Button
              fullWidth
              onClick={() => setPasswordMode('email')}
              startIcon={<EmailIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.5,
                bgcolor: passwordMode === 'email' ? '#00C896' : 'transparent',
                color: passwordMode === 'email' ? '#1a1d2e' : '#9ca3af',
                border: `1px solid ${passwordMode === 'email' ? '#00C896' : '#3a3d52'}`,
                fontWeight: 600,
                '&:hover': { bgcolor: passwordMode === 'email' ? '#7cc70d' : 'rgba(0, 200, 150,0.08)' },
              }}
            >
              Send Reset Email
            </Button>
            <Button
              fullWidth
              onClick={() => setPasswordMode('direct')}
              startIcon={<VpnKey />}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.5,
                bgcolor: passwordMode === 'direct' ? '#00C896' : 'transparent',
                color: passwordMode === 'direct' ? '#1a1d2e' : '#9ca3af',
                border: `1px solid ${passwordMode === 'direct' ? '#00C896' : '#3a3d52'}`,
                fontWeight: 600,
                '&:hover': { bgcolor: passwordMode === 'direct' ? '#7cc70d' : 'rgba(0, 200, 150,0.08)' },
              }}
            >
              Set Password Directly
            </Button>
          </Box>

          {passwordMode === 'email' ? (
            <Alert severity="info" sx={{ bgcolor: 'rgba(33,150,243,0.1)', color: '#9ca3af', border: '1px solid rgba(33,150,243,0.3)', '& .MuiAlert-icon': { color: '#2196F3' } }}>
              A secure password-reset link will be emailed to <strong style={{ color: '#fff' }}>{passwordDialogUser?.email}</strong>. They click it, choose a new password, and sign in — no further admin action needed. Recommended for security.
            </Alert>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(255,193,7,0.1)', color: '#9ca3af', border: '1px solid rgba(255,193,7,0.3)', '& .MuiAlert-icon': { color: '#FFC107' } }}>
                You'll set a new password and must communicate it to the user securely (in person / encrypted message). They can change it later.
              </Alert>
              <TextField
                fullWidth
                label="New password"
                type={showDirectPassword ? 'text' : 'password'}
                value={directPassword}
                onChange={(e) => { setDirectPassword(e.target.value); setPasswordError(''); }}
                disabled={passwordSaving}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#fff' }, '& label': { color: '#9ca3af' }, '& fieldset': { borderColor: '#3a3d52' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowDirectPassword((v) => !v)} edge="end" size="small" sx={{ color: '#9ca3af' }}>
                        {showDirectPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm password"
                type={showDirectPassword ? 'text' : 'password'}
                value={directPasswordConfirm}
                onChange={(e) => { setDirectPasswordConfirm(e.target.value); setPasswordError(''); }}
                disabled={passwordSaving}
                sx={{ '& .MuiOutlinedInput-root': { color: '#fff' }, '& label': { color: '#9ca3af' }, '& fieldset': { borderColor: '#3a3d52' } }}
              />
            </Box>
          )}

          {passwordError && (
            <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.3)' }}>{passwordError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #3a3d52' }}>
          <Button onClick={closePasswordDialog} disabled={passwordSaving} sx={{ color: '#9ca3af', textTransform: 'none' }}>Cancel</Button>
          {passwordMode === 'email' ? (
            <Button
              onClick={sendResetFromDialog}
              disabled={passwordSaving}
              variant="contained"
              startIcon={passwordSaving ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
              sx={{ bgcolor: '#00C896', color: '#1a1d2e', fontWeight: 700, textTransform: 'none', borderRadius: '10px', '&:hover': { bgcolor: '#7cc70d' } }}
            >
              {passwordSaving ? 'Sending…' : 'Send Reset Email'}
            </Button>
          ) : (
            <Button
              onClick={savePasswordDirect}
              disabled={passwordSaving || !directPassword}
              variant="contained"
              startIcon={passwordSaving ? <CircularProgress size={16} color="inherit" /> : <VpnKey />}
              sx={{ bgcolor: '#00C896', color: '#1a1d2e', fontWeight: 700, textTransform: 'none', borderRadius: '10px', '&:hover': { bgcolor: '#7cc70d' } }}
            >
              {passwordSaving ? 'Saving…' : 'Update Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit profile dialog */}
      <Dialog
        open={!!editProfileUser}
        onClose={() => !editProfileSaving && closeEditProfile()}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#252838', borderRadius: '14px', border: '1px solid #3a3d52' } }}
      >
        <DialogTitle sx={{ color: '#e0e0e0', fontWeight: 700, borderBottom: '1px solid #3a3d52', pb: 1.5 }}>
          Edit profile — {editProfileUser?.name || editProfileUser?.email}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth label="Name"
              value={editProfileForm.name}
              onChange={(e) => setEditProfileForm((f) => ({ ...f, name: e.target.value }))}
              disabled={editProfileSaving}
              sx={{ '& .MuiOutlinedInput-root': { color: '#fff' }, '& label': { color: '#9ca3af' }, '& fieldset': { borderColor: '#3a3d52' } }}
            />
            <TextField
              fullWidth label="Email"
              value={editProfileForm.email}
              onChange={(e) => setEditProfileForm((f) => ({ ...f, email: e.target.value }))}
              disabled={editProfileSaving}
              helperText="Changes only update Firestore. Use 'Change password' after email change so user reconfirms."
              sx={{ '& .MuiOutlinedInput-root': { color: '#fff' }, '& label': { color: '#9ca3af' }, '& fieldset': { borderColor: '#3a3d52' }, '& .MuiFormHelperText-root': { color: '#6b7280' } }}
            />
            <TextField
              fullWidth label="Phone"
              value={editProfileForm.phone}
              onChange={(e) => setEditProfileForm((f) => ({ ...f, phone: e.target.value }))}
              disabled={editProfileSaving}
              placeholder="+49 170 1234567"
              sx={{ '& .MuiOutlinedInput-root': { color: '#fff' }, '& label': { color: '#9ca3af' }, '& fieldset': { borderColor: '#3a3d52' } }}
            />
          </Box>
          {editProfileError && (
            <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.3)' }}>{editProfileError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #3a3d52' }}>
          <Button onClick={closeEditProfile} disabled={editProfileSaving} sx={{ color: '#9ca3af', textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={saveProfileEdit}
            disabled={editProfileSaving}
            variant="contained"
            startIcon={editProfileSaving ? <CircularProgress size={16} color="inherit" /> : <ManageAccounts />}
            sx={{ bgcolor: '#00C896', color: '#1a1d2e', fontWeight: 700, textTransform: 'none', borderRadius: '10px', '&:hover': { bgcolor: '#7cc70d' } }}
          >
            {editProfileSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  );
};

export default UsersPage;
